//*Importing from libraries
import React, { useEffect, useState } from "react";

//*Importing components from react-bootstrap
import { Button, Form, Spinner, Collapse } from "react-bootstrap";

//*Importing components
import { Attention } from "../../../component/base/Attention";
import { ErrorMessages } from "../../../component/common/ErrorMessages";

//*Importing hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//*Importing icons from material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//*Importing custom hooks
import { useOrganizations } from "../../../hooks/organization/useOrganization";

//*Importing constants
import { STORAGE_URL } from "../../../constants/consts";

//*Interface for detailed admin information
type Admin = {
  id: string;
  person: {
    id: string;
    name: string;
    email: string;
  };
  organization: {
    id: string;
    name: string;
  };
  role: string;
  createdAt: string;
  updatedAt: string;
};

type OrganizationsAdmins = Admin[];

/**
 * Component for rendering an organization form.
 *
 * @param props - The properties for the component.
 * @param props.editMode - Optional boolean indicating if the form is in edit mode.
 *
 * @returns A JSX element representing the organization form.
 *
 * @remarks
 * This component handles both the creation and editing of an organization.
 * It includes form fields for the organization's name, description, logo, and administrator details.
 *
 * @example
 * ```tsx
 * <OrganizationForm editMode={true} />
 * ```
 *
 * @component
 *
 * @requires useState
 * @requires useEffect
 * @requires useNavigate
 * @requires useParams
 * @requires useOrganizations
 * @requires ErrorMessages
 * @requires Form
 * @requires Button
 * @requires Collapse
 * @requires Spinner
 * @requires STORAGE_URL
 * @requires ArrowBack
 * @requires SaveRounded
 * @requires Attention
 *
 * @function
 * @name OrganizationForm
 */
export function OrganizationForm(props: { editMode?: boolean }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logoPath: null as File | null,
    adminId: "",
    adminName: "",
    adminEmail: "",
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [admins, setAdmins] = useState<OrganizationsAdmins>([]);
  const [showAdminCreation, setShowAdminCreation] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const token = localStorage.getItem("token") || "";
  const {
    loading,
    error,
    createOrganization,
    updateOrganization,
    getOrganizationsById,
    getAdminsByOrganization,
  } = useOrganizations(token);

  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (props.editMode && params.id) {
      getOrganizationsById(params.id).then((organization) => {
        if (organization) {
          setFormData({
            name: organization.name || "",
            description: organization.description || "",
            logoPath: null,
            adminId: organization.responsibleId || "", // Usando o ID correto do responsável
            adminName: organization.responsible?.name || "",
            adminEmail: organization.responsible?.email || "",
          });

          if (organization.logoPath) {
            setLogoPreview(`${STORAGE_URL}${organization.logoPath}`);
          }
        }
      });

      getAdminsByOrganization(params.id).then((admins: Admin[]) => {
        setAdmins(admins);
      });
    }
  }, [props.editMode, params.id]);

  // Ensure adminId is set only when admins list is loaded
  useEffect(() => {
    if (admins.length > 0 && !formData.adminId) {
      const adminResponsavel = admins.find(
        (admin) => admin.id === formData.adminId
      );
      if (adminResponsavel) {
        setFormData({
          ...formData,
          adminId: adminResponsavel.id,
        });
      }
    }
  }, [admins]);

  const clearErrors = () => {
    setErrorMessages([]);
  };

  /**
   * Handles the form submission to save organization content.
   *
   * @param e - The form event triggered by the submission.
   *
   * This function performs the following actions:
   * - Prevents the default form submission behavior.
   * - Validates the form data and collects any validation errors.
   * - If there are validation errors, sets the error messages and exits.
   * - Creates a FormData object and appends the form data to it.
   * - If in edit mode, appends the responsibleId and updates the organization.
   * - If not in edit mode, appends the admin details and creates a new organization.
   * - Navigates to the organizations page upon successful save.
   * - Catches and logs any errors during the save process and sets an error message.
   */
  const saveContent = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push("Nome da organização é obrigatório.");
    }
    if (!props.editMode && !formData.adminName.trim()) {
      newErrors.push("Nome do administrador é obrigatório.");
    }
    if (!props.editMode && !formData.adminEmail.trim()) {
      newErrors.push("E-mail do administrador é obrigatório.");
    }
    if (
      !props.editMode &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)
    ) {
      newErrors.push("E-mail do administrador inválido.");
    }

    if (newErrors.length > 0) {
      setErrorMessages(newErrors);
      return;
    }

    const form = new FormData();
    form.append("name", formData.name);
    form.append("description", formData.description || "");

    if (formData.logoPath instanceof File) {
      form.append("logoPath", formData.logoPath);
    }

    try {
      if (props.editMode && params.id) {
        form.append("responsibleId", formData.adminId);
        await updateOrganization({ id: params.id, organizationData: form });
      } else {
        form.append("adminName", formData.adminName);
        form.append("adminEmail", formData.adminEmail);
        await createOrganization(form);
      }
      navigate("/organizations");
    } catch (err) {
      console.error("Error saving organization:", err);
      setErrorMessages([
        "Erro ao salvar a organização. Por favor, tente novamente.",
      ]);
    }
  };

  /**
   * Handles the change event for the logo input field.
   *
   * This function is triggered when the user selects a file for the logo.
   * It updates the form data with the selected file and generates a preview of the logo.
   *
   * @param e - The change event triggered by the file input field.
   */
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, logoPath: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({ ...formData, logoPath: null });
      setLogoPreview(null);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="py-5 px-3">
            <div className="card-body" id="card">
              <h4 className="mt-0 header-title">
                {props.editMode ? "Editar" : "Cadastrar"} Organização
              </h4>
              <p>
                {props.editMode
                  ? "Edite as informações da organização"
                  : "Cadastre uma nova organização no sistema"}
              </p>
              <br />

              <ErrorMessages
                messages={errorMessages}
                clearErrors={clearErrors}
              />

              {!loading ? (
                <Form onSubmit={saveContent}>
                  <div className="row">
                    <div className="col-lg-6 col-md-12">
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Nome da Organização <Attention />
                        </Form.Label>
                        <Form.Control
                          placeholder="Nome"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          required
                        />
                      </Form.Group>
                    </div>

                    <div className="col-lg-6 col-md-12">
                      <Form.Group className="mb-3">
                        <Form.Label>Descrição (Opcional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          placeholder="Descrição da organização"
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              description: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6 col-md-12">
                      <Form.Group className="mb-3">
                        <Form.Label>Logo</Form.Label>
                        <Form.Control type="file" onChange={handleLogoChange} />
                        {logoPreview && (
                          <div className="mt-3 text-center">
                            <img
                              src={logoPreview}
                              alt="Logo Preview"
                              className="img-fluid rounded shadow-sm"
                              style={{ maxWidth: "200px", height: "auto" }}
                            />
                          </div>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  <h5 className="mt-4">Responsável (Administrador)</h5>

                  {props.editMode ? (
                    <div className="row">
                      <div className="col-lg-6 col-md-12">
                        <Form.Group className="mb-3">
                          <Form.Label>Selecione um administrador</Form.Label>
                          <Form.Select
                            value={formData.adminId}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                adminId: e.target.value,
                              })
                            }
                          >
                            <option value="">Selecione um administrador</option>
                            {admins.map((admin: Admin) => (
                              <option key={admin.id} value={admin.id}>
                                {admin.person.name} ({admin.person.email})
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-12">
                        <Button
                          variant="link"
                          onClick={() =>
                            setShowAdminCreation(!showAdminCreation)
                          }
                          aria-expanded={showAdminCreation}
                          className="mb-3 text-decoration-none fw-bold"
                        >
                          {showAdminCreation
                            ? "Ocultar formulário de criação do administrador"
                            : "Criar um novo administrador"}
                        </Button>
                        <Collapse in={showAdminCreation}>
                          <div>
                            <div className="row">
                              <div className="col-lg-6 col-md-12">
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Nome do Administrador <Attention />
                                  </Form.Label>
                                  <Form.Control
                                    placeholder="Nome"
                                    value={formData.adminName}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        adminName: e.target.value,
                                      })
                                    }
                                    required
                                  />
                                </Form.Group>
                              </div>

                              <div className="col-lg-6 col-md-12">
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    E-mail do Administrador <Attention />
                                  </Form.Label>
                                  <Form.Control
                                    placeholder="E-mail"
                                    value={formData.adminEmail}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        adminEmail: e.target.value,
                                      })
                                    }
                                    required
                                  />
                                </Form.Group>
                              </div>
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    </div>
                  )}

                  <div className="w-100 d-flex justify-content-between mt-3">
                    <Button
                      variant="secondary"
                      className="d-flex align-items-center"
                      onClick={() => navigate("/organizations")}
                    >
                      <ArrowBack className="me-2" /> Voltar
                    </Button>
                    <Button
                      variant="success"
                      type="submit"
                      disabled={loading}
                      className="d-flex align-items-center"
                    >
                      <SaveRounded className="me-2" /> Salvar
                    </Button>
                  </div>
                </Form>
              ) : (
                <div className="d-flex justify-content-center align-items-center mt-3">
                  <Spinner animation="border" variant="primary" role="status" />
                </div>
              )}

              {error && (
                <ErrorMessages
                  messages={[error]}
                  clearErrors={() => {
                    /* clear error logic */
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
